// Local data layer (IndexedDB via Dexie). All data lives on-device; no server.
// Mirrors the refined brief's model. Item analysis and MPS are derived on demand,
// never stored.
import Dexie, { type Table } from 'dexie';

export interface ClassRecord {
  id: string;
  name: string;
  gradeLevel: string;
  schoolYear: string;
  createdAt: number;
}

export interface StudentRecord {
  id: string;
  classId: string;
  /** full 12-digit LRN — bubbled directly on the sheet AND the roster key. */
  lrn: string;
  name: string;
  sex?: 'M' | 'F' | '';
}

/** Fixed preset item counts. A quiz picks one; the app loads assets/layouts/{items}.json. */
export const PRESETS = [20, 25, 30, 35, 40, 50, 60, 75, 100] as const;
export type Preset = (typeof PRESETS)[number];

export interface QuizRecord {
  id: string;
  title: string;
  subject: string;
  gradeLevel: string;
  /** one of PRESETS; determines the layout map and item count. Choices are always 4. */
  items: Preset;
  /** 'local' = teacher-created; 'testify' = pulled from a TESTIFY exam (§13). */
  origin?: 'local' | 'testify';
  /** for origin 'testify': the parent TestifyExam and this subject's TESTIFY test_id. */
  testifyExamId?: string;
  testId?: string;
  createdAt: number;
}

/** A quiz administered to one class on a date. One quiz → many assignments. */
export type IdentificationMode = 'roster' | 'captured_name' | 'lrn_direct';

export interface QuizAssignmentRecord {
  id: string;
  quizId: string;
  classId: string;
  dateAdministered: string; // ISO yyyy-mm-dd
  /** 'roster' matches the bubbled LRN to a roster student; 'lrn_direct' reads the
   *  bubbled 12-digit LRN with no roster (§7B); 'captured_name' has no LRN (§7A,
   *  TESTIFY-ineligible). Default 'roster'. */
  identificationMode?: IdentificationMode;
  /** values for the exam's required custom fields (§13.4), one per assignment. */
  customFields?: Record<string, string>;
  createdAt: number;
}

export interface AnswerKeyRecord {
  id: string;
  quizId: string; // one key per quiz
  /** correct option per item, e.g. { 1: 'B', 2: 'A' }. Empty for a hashed (TESTIFY) key. */
  answers: Record<number, string>;
  /** optional points per item; defaults to 1 each when absent. */
  points?: Record<number, number>;
  /** TESTIFY hashed key (§13.2): per item, per option letter → key_hash. '0' = wrong,
   *  a salted SHA-256 = correct. Scoring is a zero/non-zero check; never hashed client-side. */
  hashedAnswers?: Record<number, Record<string, string>>;
  /** true for a TESTIFY key: no manual tap-in, no scan-the-key, never displayed/edited. */
  locked?: boolean;
  /** TESTIFY upload map (§13.8): per item number → its ques_id and the choice_id of
   *  each option letter. The /upload body needs quesId + selectedChoiceId (not
   *  letters), so this is captured at import and read by the outbox. */
  uploadMap?: Record<number, { quesId: number; choiceIds: Record<string, number> }>;
}

/**
 * One row of a Table of Specifications: a learning competency and the items that
 * measure it. `items` is what turns item-level scores into competency-level
 * results — a TOS without item placement can't be mapped.
 *
 * The competency IS its description. The new curriculum dropped competency codes,
 * and TESTIFY keys on the Learning Competency Description, so there is no code
 * field here; compact UI/CSV labels ("C1", "C2") are positional and derived, never
 * stored.
 */
export interface CompetencyRecord {
  /** the Learning Competency Description, as written in the TOS. Its identity —
   *  unique within the map. */
  description: string;
  /** 1-based item numbers measuring this competency. An item belongs to at most
   *  one competency, so per-competency scores never double-count. */
  items: number[];
  /** optional RBT cognitive level (Remembering … Creating) from the TOS. */
  cognitiveLevel?: string;
}

/**
 * A quiz's Table of Specifications (one map per quiz, like the answer key).
 * Derived reports (per-competency mastery, per-learner intervention tiers) are
 * computed on demand from this + the results, never stored.
 *
 * There is no configurable cut-off: DepEd's passing mark of 75 is operative and
 * the instructional-response bands are pinned to absolute score ranges around it
 * (see MASTERY_CUTOFF / interventionFor in analysis/competencyMap.ts).
 */
export interface CompetencyMapRecord {
  id: string;
  quizId: string; // one map per quiz
  competencies: CompetencyRecord[];
  /** 'testify' = auto-built from the competency descriptions in the exam bundle. */
  origin?: 'local' | 'testify';
  updatedAt: number;
}

/**
 * School / teacher details for printed and exported reports. Exactly one row
 * (id 'app'). Typed once, then reused on every report header, signature block and
 * the school/teacher columns of the raw CSV.
 */
export interface SettingsRecord {
  id: 'app';
  schoolName: string;
  schoolId: string;
  division: string;
  district: string;
  teacherName: string;
  teacherPosition: string;
  principalName: string;
  principalPosition: string;
  updatedAt: number;
}

/** Empty settings — what the reports use before the teacher fills them in. */
export const EMPTY_SETTINGS: SettingsRecord = {
  id: 'app',
  schoolName: '',
  schoolId: '',
  division: '',
  district: '',
  teacherName: '',
  teacherPosition: 'Teacher',
  principalName: '',
  principalPosition: 'School Principal',
  updatedAt: 0,
};

/** A TESTIFY exam pulled via the import flow (§13.5). One exam → many subject Quizzes. */
export interface TestifyExamRecord {
  id: string;
  examCode: string;
  /** the exam's name in TESTIFY (e.g. "STE Entrance Exam 2026"). Optional only
   *  because exams imported before this field existed don't carry it. */
  examTitle?: string;
  /** exam-level grade level and school year as TESTIFY reports them (TESTIFY's
   *  wire field is `academic_year`; GRADIFY says school year throughout). */
  gradeLevel?: string;
  schoolYear?: string;
  token: string; // exam_school token (bearer)
  schoolId: string;
  schoolCode: string;
  schoolName: string;
  compileVersion: string;
  /** numeric compile_version for the /upload staleness check (§13.8); the string
   *  above is kept for display. Optional for exams imported before this field. */
  compileVersionNum?: number;
  signingKey: string; // HMAC material from /export (used only when the upload client is built)
  requiredCustomFields: Array<{ key: string; label: string; required: boolean }>;
  validatedAt: number;
}

export interface ResultItem {
  q: number;
  chosen: string | null;
  flags: string[];
}

export interface ResultRecord {
  id: string;
  /** scopes the result to one class's run of a quiz. */
  quizAssignmentId: string;
  /** null until matched to a roster student (roster mode) or when captured-name. */
  studentId: string | null;
  /** typed name for a captured_name result (§7A); null in roster mode. */
  capturedName?: string | null;
  /** cropped Name-box image (PNG) kept for re-tagging; captured_name mode only. */
  capturedNameImage?: Blob | null;
  /** 12-digit LRN read from the sheet's ID grid (may contain '?' for ambiguous digits). */
  idDigitsRead: string;
  /** chosen option + flags per item (the engine's raw read; no scoring baked in). */
  rawAnswers: ResultItem[];
  /** null until scored against the quiz's answer key. */
  score: number | null;
  maxScore: number | null;
  /** true if any item is flagged or the student is unmatched — needs teacher review. */
  needsReview: boolean;
  /** TESTIFY sync state (§13.5): 'local_only' = not TESTIFY-bound; 'pending' = queued
   *  for upload; 'synced' / 'rejected' set by the (future) upload client. */
  syncStatus?: 'local_only' | 'pending' | 'synced' | 'rejected';
  syncError?: string;
  capturedAt: number;
}

export class OmrDb extends Dexie {
  classes!: Table<ClassRecord, string>;
  students!: Table<StudentRecord, string>;
  quizzes!: Table<QuizRecord, string>;
  quizAssignments!: Table<QuizAssignmentRecord, string>;
  answerKeys!: Table<AnswerKeyRecord, string>;
  results!: Table<ResultRecord, string>;
  testifyExams!: Table<TestifyExamRecord, string>;
  competencyMaps!: Table<CompetencyMapRecord, string>;
  settings!: Table<SettingsRecord, string>;

  constructor(name = 'omr-scan') {
    super(name);
    this.version(1).stores({
      classes: 'id, schoolYear, name',
      students: 'id, classId, [classId+studentNo], name',
      quizzes: 'id, createdAt',
      quizAssignments: 'id, quizId, classId, [quizId+classId]',
      answerKeys: 'id, quizId',
      results: 'id, quizAssignmentId, studentId, [quizAssignmentId+studentId], capturedAt',
    });
    // v2: students are identified by the 12-digit LRN bubbled on the sheet. Reindex
    // students by lrn (+[classId+lrn] for per-class scan matching); v1's legacy
    // per-class number index (kept verbatim above, as version history requires) is
    // dropped here.
    this.version(2).stores({
      students: 'id, classId, lrn, [classId+lrn], name',
    });
    // v3: TESTIFY integration (§13). Add the testifyExams store; index quizzes by
    // testifyExamId and results by syncStatus (outbox drain, when built).
    this.version(3).stores({
      quizzes: 'id, createdAt, testifyExamId',
      results: 'id, quizAssignmentId, studentId, [quizAssignmentId+studentId], syncStatus, capturedAt',
      testifyExams: 'id, examCode',
    });
    // v4: Table of Specifications per quiz — the item→competency map that makes
    // per-competency learning gaps computable. Keyed by quizId (one map per quiz).
    this.version(4).stores({
      competencyMaps: 'id, quizId',
    });
    // v5: school / teacher details for report headers and signature blocks.
    // Single row, keyed 'app'.
    this.version(5).stores({
      settings: 'id',
    });
  }
}

/** Default shared instance (browser). Tests construct their own with fake-indexeddb. */
export const db = new OmrDb();
